import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Button, Card, message, Col } from 'antd'
import _ from 'lodash'
import dayjs from 'dayjs'
import { getConfigQuery } from '@/services/orderQuery/byBatch'
import {
  getCGradeList,
  getFactoryGroup,
  getFactoryCode,
  getSeasonList,
  getFactoryGroupMco
} from '@/services/cGrade/cGrade'
import AntdTable from '@/components/table/AntdTable'
import SearchBar from '@/components/searchbar/SearchBar'
import ImagesCom from '@/pages/cGrade/components/ImagesCom'
import {
  paginationInit,
  formatMessage,
  initValueAndLabel,
  uniqueFun,
  formatStartTime,
  formatEndTime,
  dateFormatYearMonthDay
} from '@/utils/common'

/** 防止 null/undefined 导致渲染异常 */
const safeText = v => (v === null || v === undefined || v === 'null' ? '' : String(v))

/** 图片单元格：只使用新字段 pictoryUrlMapList */
const ImageCell = React.memo(function ImageCell({ mapList, detail }) {
  const ls = useMemo(() => {
    const arr = Array.isArray(mapList) ? mapList : []
    return arr
      .map(m => ({
        src: m?.url,
        retakeFlag: !!m?.retakeFlag,
        detail
      }))
      .filter(x => !!x.src)
  }, [mapList, detail])

  return <ImagesCom propsImageLs={ls} maxShow={5} />
})

const ListModePage = () => {
  const formRef = useRef(null)

  const [tableList, setTableList] = useState([])
  const [searchList, setSearchList] = useState([])
  const [pagination, setPagination] = useState(paginationInit)

  /** sorter 用 ref，减少 render */
  const sorterRef = useRef([])

  /** 并发控制：只让最后一次请求生效 */
  const reqIdRef = useRef(0)

  const cGradeCopy = useMemo(
    () => ({
      select: formatMessage('languageObj.cGrade.select'),
      enter: formatMessage('languageObj.cGrade.enter'),
      startDate: formatMessage('languageObj.cGrade.startDate'),
      endDate: formatMessage('languageObj.cGrade.endDate'),
      search: formatMessage('languageObj.cGrade.search'),
      reset: formatMessage('languageObj.cGrade.reset'),
      selectAll: formatMessage('languageObj.cGrade.selectAll'),
      last7: formatMessage('languageObj.cGrade.last7'),
      last30: formatMessage('languageObj.cGrade.last30'),
      last90: formatMessage('languageObj.cGrade.last90'),
      queryError: formatMessage('languageObj.cGrade.queryError'),
      noData: formatMessage('languageObj.cGrade.noData')
    }),
    []
  )

  const cGradeRangePresets = useMemo(
    () => [
      {
        label: cGradeCopy.last7,
        value: [dayjs().add(-7, 'd'), dayjs()]
      },
      {
        label: cGradeCopy.last30,
        value: [dayjs().add(-30, 'd'), dayjs()]
      },
      {
        label: cGradeCopy.last90,
        value: [dayjs().add(-90, 'd'), dayjs()]
      }
    ],
    [cGradeCopy]
  )

  /** ===== 级联：MCO 变更 ===== */
  const onMcoChange = useCallback((mcoSelectedArray = []) => {
    const mcoPayload = (mcoSelectedArray || []).join(',')

    return Promise.all([getFactoryGroup({ mco: mcoPayload }), getFactoryCode({ mco: mcoPayload })])
      .then(([fgRes, vendorRes]) => {
        const fgOptions = uniqueFun(initValueAndLabel(fgRes?.resultData || []))
        const vendorOptions = uniqueFun(initValueAndLabel(vendorRes?.resultData || []))

        setSearchList(prev =>
          prev.map(item => {
            if (item.key === 'factoryGroup') return { ...item, selectList: fgOptions }
            if (item.key === 'vendorCd') return { ...item, selectList: vendorOptions }
            return item
          })
        )

        const f = formRef.current
        if (f && typeof f.setValue === 'function') {
          f.setValue({ factoryGroup: [], vendorCd: [] })
        }
      })
      .catch(() => {})
  }, [])

  /** ===== 级联：FactoryGroup 变更 ===== */
  const onFactoryGroupChange = useCallback((factoryGroupSelectedArray = []) => {
    const f = formRef.current
    const allValues = f && typeof f.getValue === 'function' ? f.getValue() || {} : {}
    const mcoSelectedArray = allValues.mco || []

    const mcoPayload = (mcoSelectedArray || []).join(',')
    const factoryGroupPayload = (factoryGroupSelectedArray || []).join(',')

    return getFactoryCode({ mco: mcoPayload, factoryGroup: factoryGroupPayload })
      .then(vendorRes => {
        const vendorOptions = uniqueFun(initValueAndLabel(vendorRes?.resultData || []))

        setSearchList(prev =>
          prev.map(item => {
            if (item.key === 'vendorCd') return { ...item, selectList: vendorOptions }
            return item
          })
        )

        if (f && typeof f.setValue === 'function') {
          f.setValue({ vendorCd: [] })
        }
      })
      .catch(() => {})
  }, [])

  /** ===== Search schema：useMemo 保持引用稳定 ===== */
  const searchSchemaBase = useMemo(() => {
    return [
      {
        type: 'select',
        label: 'languageObj.cGrade.factoryGroup',
        key: 'factoryGroup',
        initialValues: [],
        mode: 'multiple',
        maxTagCount: 2,
        showAll: true,
        placeholder: cGradeCopy.select,
        allLabel: cGradeCopy.selectAll,
        onChange: onFactoryGroupChange,
        selectList: []
      },
      {
        type: 'select',
        label: 'languageObj.cGrade.vendorCd',
        key: 'vendorCd',
        initialValues: [],
        mode: 'multiple',
        maxTagCount: 1,
        showAll: true,
        placeholder: cGradeCopy.select,
        allLabel: cGradeCopy.selectAll,
        selectList: []
      },
      {
        type: 'select',
        label: 'languageObj.cGrade.mco',
        key: 'mco',
        initialValues: [],
        mode: 'multiple',
        maxTagCount: 1,
        showAll: true,
        placeholder: cGradeCopy.select,
        allLabel: cGradeCopy.selectAll,
        onChange: onMcoChange,
        selectList: []
      },
      {
        type: 'timeRange',
        label: 'languageObj.cGrade.returnDate',
        key: 'returnDate',
        showRangePresets: true,
        presets: cGradeRangePresets,
        placeholder: [cGradeCopy.startDate, cGradeCopy.endDate],
        format: dateFormatYearMonthDay,
        disabledDate: current => current && current > dayjs().endOf('day'),
        initialValues: []
      },
      {
        type: 'timeRange',
        label: 'languageObj.cGrade.salesOrderDate',
        key: 'salesOrderDate',
        showRangePresets: true,
        presets: cGradeRangePresets,
        placeholder: [cGradeCopy.startDate, cGradeCopy.endDate],
        format: dateFormatYearMonthDay,
        disabledDate: current => current && current > dayjs().endOf('day'),
        initialValues: []
      },
      {
        type: 'select',
        label: 'languageObj.cGrade.salesOrderSeason',
        key: 'salesOrderSeason',
        allowClear: true,
        initialValues: [],
        mode: 'multiple',
        maxTagCount: 1,
        showAll: true,
        placeholder: cGradeCopy.select,
        allLabel: cGradeCopy.selectAll,
        selectList: []
      },
      {
        type: 'select',
        label: 'languageObj.cGrade.gcCategory',
        key: 'gcCategory',
        initialValues: [],
        mode: 'multiple',
        maxTagCount: 1,
        showAll: true,
        placeholder: cGradeCopy.select,
        allLabel: cGradeCopy.selectAll,
        selectList: []
      },
      {
        type: 'input',
        label: 'languageObj.cGrade.model',
        key: 'model',
        initialValues: '',
        placeholder: cGradeCopy.enter
      },
      {
        type: 'input',
        label: 'languageObj.cGrade.styleCode',
        key: 'styleCode',
        initialValues: '',
        placeholder: cGradeCopy.enter
      },
      {
        type: 'input',
        label: 'languageObj.cGrade.productCode',
        key: 'productCode',
        initialValues: '',
        placeholder: cGradeCopy.enter
      },
      {
        type: 'select',
        label: 'languageObj.cGrade.defectiveReason',
        key: 'defectTypes',
        initialValues: [],
        mode: 'multiple',
        maxTagCount: 1,
        showAll: true,
        placeholder: cGradeCopy.select,
        allLabel: cGradeCopy.selectAll,
        selectList: []
      }
    ]
  }, [cGradeCopy, cGradeRangePresets, onFactoryGroupChange, onMcoChange])

  /** ===== 表格 columns：useMemo 避免重建 ===== */
  const tableColumns = useMemo(() => {
    return [
      {
        title: formatMessage('languageObj.cGrade.productCode'),
        dataIndex: 'productCode',
        width: 100,
        sorter: { multiple: 1 },
        render: text => <div className="word_break_all">{safeText(text)}</div>
      },
      {
        title: formatMessage('languageObj.cGrade.model'),
        dataIndex: 'model',
        width: 100,
        render: text => <div className="word_break_all">{safeText(text)}</div>
      },
      {
        title: formatMessage('languageObj.cGrade.defectiveReason'),
        dataIndex: 'defectType',
        width: 100,
        sorter: { multiple: 2 },
        render: text => <div className="word_break_all">{safeText(text)}</div>
      },
      {
        title: formatMessage('languageObj.cGrade.productThumbnail'),
        dataIndex: 'pictoryUrlMapList',
        width: 340,
        render: (_, record) => <ImageCell mapList={record?.pictoryUrlMapList} detail={record} />
      },
      {
        title: formatMessage('languageObj.cGrade.vendorCd'),
        dataIndex: 'vendorCd',
        width: 100,
        render: text => <div className="word_break_all">{safeText(text)}</div>
      },
      {
        title: formatMessage('languageObj.cGrade.factoryGroup'),
        dataIndex: 'factoryGroup',
        width: 80,
        render: text => <div className="word_break_all">{safeText(text)}</div>
      }
    ]
  }, [])

  /** ===== options 格式化 ===== */
  const formatOptions = useCallback(ls => {
    const arr = ls || []
    return arr.map(x => ({ label: x.configValue, value: x.configKey }))
  }, [])

  /** ===== 初始化下拉 ===== */
  const getConfigQueryFun = useCallback(() => {
    const schema = searchSchemaBase.map(x => ({ ...x }))

    const defaultMcoSelected = schema.find(i => i.key === 'mco')?.initialValues || []
    const mcoPayload = Array.isArray(defaultMcoSelected) ? defaultMcoSelected.join(',') : defaultMcoSelected || ''

    const defaultFactoryGroupSelected = schema.find(i => i.key === 'factoryGroup')?.initialValues || []
    const factoryGroupPayload = Array.isArray(defaultFactoryGroupSelected)
      ? defaultFactoryGroupSelected.join(',')
      : defaultFactoryGroupSelected || ''

    return Promise.all([
      getFactoryGroup({ mco: mcoPayload }),
      getFactoryCode({ mco: mcoPayload, factoryGroup: factoryGroupPayload }),
      getSeasonList(),
      getConfigQuery({ configType: 'defect type' }),
      getConfigQuery({ configType: 'c_query_gc_category' }),
      getFactoryGroupMco()
    ])
      .then(res => {
        const fgRaw = res[0]?.resultData || []
        const vendorRaw = res[1]?.resultData || []
        const seasonRaw = res[2]?.resultData || []
        const defectRaw = res[3]?.resultData || []
        const gcRaw = res[4]?.resultData || []
        const mcoRaw = res[5]?.resultData || []

        const factoryGroupOptions = uniqueFun(initValueAndLabel(fgRaw))
        const vendorOptions = uniqueFun(initValueAndLabel(vendorRaw))
        const seasonOptions = uniqueFun(initValueAndLabel(seasonRaw))
        const defectOptions = uniqueFun(formatOptions(defectRaw))
        const gcOptions = uniqueFun(formatOptions(gcRaw))
        const mcoOptions = uniqueFun(initValueAndLabel(mcoRaw))

        const next = schema.map(item => {
          if (item.key === 'mco') return { ...item, selectList: mcoOptions }
          if (item.key === 'factoryGroup') return { ...item, selectList: factoryGroupOptions }
          if (item.key === 'vendorCd') return { ...item, selectList: vendorOptions }
          if (item.key === 'salesOrderSeason') return { ...item, selectList: seasonOptions }
          if (item.key === 'defectTypes') return { ...item, selectList: defectOptions }
          if (item.key === 'gcCategory') return { ...item, selectList: gcOptions }
          return item
        })

        setSearchList(next)
      })
      .catch(() => {})
  }, [formatOptions, searchSchemaBase])

  /** ===== 组装参数 ===== */
  const buildParams = useCallback(() => {
    const f = formRef.current
    const info = f && typeof f.getValue === 'function' ? f.getValue() || {} : {}

    const { returnDate, salesOrderDate } = info
    const returnDateStart = formatStartTime(returnDate, 1)
    const returnDateEnd = formatEndTime(returnDate, 1)
    const salesOrderDateStart = formatStartTime(salesOrderDate, 1)
    const salesOrderDateEnd = formatEndTime(salesOrderDate, 1)

    const factoryGroup = Array.isArray(info.factoryGroup) ? info.factoryGroup.join(',') : ''
    const vendorCd = Array.isArray(info.vendorCd) ? info.vendorCd.join(',') : ''
    const mco = Array.isArray(info.mco) ? info.mco.join(',') : ''
    const gcCategory = Array.isArray(info.gcCategory) ? info.gcCategory.join(',') : ''
    const defectTypes = Array.isArray(info.defectTypes) ? info.defectTypes.join(',') : ''
    const salesOrderSeason = Array.isArray(info.salesOrderSeason) ? info.salesOrderSeason.join(',') : ''

    const sorterList = sorterRef.current || []
    const sortedSorterList = _.sortBy(sorterList, item => {
      const column = tableColumns.find(col => col.dataIndex === item.field)
      return column && column.sorter && column.sorter.multiple
    })

    const params = {
      ...info,
      factoryGroup,
      vendorCd,
      mco,
      gcCategory,
      defectTypes,
      salesOrderSeason,
      returnDateStart,
      returnDateEnd,
      salesOrderDateStart,
      salesOrderDateEnd
    }

    if (sortedSorterList.length > 0) {
      params.sortDirection = sortedSorterList.map(item => (item.order === 'ascend' ? 'ASC' : 'DESC'))
      params.sortProperty = sortedSorterList.map(item => item.field)
    } else {
      params.sortDirection = []
      params.sortProperty = []
    }

    return params
  }, [tableColumns])

  /** ===== 查询：最后一次请求生效 ===== */
  const fetchList = useCallback(
    async nextPagination => {
      const reqId = ++reqIdRef.current
      const pg = nextPagination || pagination

      const base = buildParams()
      const params = {
        ...base,
        pageSize: pg.pageSize,
        page: pg.currentPage - 1
      }

      try {
        const res = await getCGradeList(params)
        if (reqId !== reqIdRef.current) return

        const dt = res?.resultData || {}
        const records = dt.content || []
        setTableList(records)
        setPagination(prev => ({ ...prev, total: dt.totalElements || 0 }))
      } catch (error) {
        if (reqId !== reqIdRef.current) return
        // eslint-disable-next-line no-console
        console.error('Query failed:', error)
        message.error(cGradeCopy.queryError)
      }
    },
    [buildParams, cGradeCopy, pagination]
  )

  /** ===== 初始化下拉 ===== */
  useEffect(() => {
    getConfigQueryFun()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  /** ===== 搜索 ===== */
  const searchDataBtn = useCallback(() => {
    const nextPg = { ...pagination, currentPage: 1 }
    setPagination(nextPg)
    fetchList(nextPg)
  }, [fetchList, pagination])

  /** ===== 清空 ===== */
  const searchClearDataBtn = useCallback(() => {
    const f = formRef.current
    if (f && typeof f.clearForm === 'function') {
      f.clearForm()
    }
    const nextPg = { ...pagination, currentPage: 1 }
    setPagination(nextPg)
    fetchList(nextPg)
  }, [fetchList, pagination])

  /** ===== 表格分页/排序变化 ===== */
  const handleTableChange = useCallback(
    (pagina, filters, sorter) => {
      if (Array.isArray(sorter)) {
        sorterRef.current = sorter
      } else if (sorter && sorter.field) {
        sorterRef.current = [sorter]
      } else {
        sorterRef.current = []
      }

      const nextPg = { ...pagination, currentPage: pagina.current, pageSize: pagina.pageSize }
      setPagination(nextPg)
      fetchList(nextPg)
    },
    [fetchList, pagination]
  )

  return (
    <div>
      <Card>
        <div className="search-bar">
          <SearchBar ref={formRef} searchList={searchList}>
            <Col
              xs={24}
              sm={12}
              md={12}
              lg={8}
              xl={6}
              xxl={6}
              className="right_search"
              style={{ justifyContent: 'flex-end' }}
            >
              <Button type="primary" onClick={searchDataBtn}>
                {cGradeCopy.search}
              </Button>
              <Button onClick={searchClearDataBtn}>{cGradeCopy.reset}</Button>
            </Col>
          </SearchBar>
        </div>
      </Card>

      <Card className="mt15">
        <div className="content-container">
          <AntdTable
            rowKey={(r, i) => r?.id || `${r?.productCode}_${r?.vendorCd}_${i}`}
            dataSource={tableList}
            columns={tableColumns}
            pagination={pagination}
            onChange={handleTableChange}
            scroll={{ x: '100%' }}
            locale={{ emptyText: cGradeCopy.noData }}
          />
        </div>
      </Card>
    </div>
  )
}

export default ListModePage
